import { useRef, useState } from "react";
import { AlertCircle, Lightbulb } from "lucide-react";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";

import { ProductFeedbackDialog } from "@/components/feedback/ProductFeedbackDialog";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { buildBugReportPayload, buildFeatureRequestPayload } from "@/lib/productFeedback";
import {
  deleteBugReportScreenshot,
  uploadBugReportScreenshot,
  validateBugReportScreenshot,
} from "@/lib/bugReportScreenshots";
import { useAuth } from "@/hooks/useAuth";
import { useSubmitBugReport, useSubmitFeatureRequest } from "@/hooks/useProductFeedback";
import { supabase } from "@/integrations/supabase/client";
import { getRecentClientLogs } from "@/utils/logger";

type ReporterRole = "coach" | "leader";
type ProductFeedbackMode = "bug" | "feature" | null;

interface ProductFeedbackToolbarActionsProps {
  reporterRole: ReporterRole;
  compact?: boolean;
  iconOnly?: boolean;
  className?: string;
}

export function ProductFeedbackToolbarActions({
  reporterRole,
  compact = false,
  iconOnly = false,
  className,
}: ProductFeedbackToolbarActionsProps) {
  const location = useLocation();
  const { user } = useAuth();
  const submitBugReport = useSubmitBugReport();
  const submitFeatureRequest = useSubmitFeatureRequest();
  const [openMode, setOpenMode] = useState<ProductFeedbackMode>(null);
  const [bugSummary, setBugSummary] = useState("");
  const [bugDetails, setBugDetails] = useState("");
  const [bugAttachmentFile, setBugAttachmentFile] = useState<File | null>(null);
  const [featureSummary, setFeatureSummary] = useState("");
  const [featureDetails, setFeatureDetails] = useState("");
  const [featureSubmitSucceeded, setFeatureSubmitSucceeded] = useState(false);
  const [isUploadingBugAttachment, setIsUploadingBugAttachment] = useState(false);
  const featureDialogSessionRef = useRef(0);
  const bugSubmitInFlightRef = useRef(false);

  const isSubmitting = submitBugReport.isPending || submitFeatureRequest.isPending || isUploadingBugAttachment;

  const resetMode = (mode: ProductFeedbackMode) => {
    if (mode === "bug") {
      setBugSummary("");
      setBugDetails("");
      setBugAttachmentFile(null);
      return;
    }

    if (mode === "feature") {
      featureDialogSessionRef.current += 1;
      setFeatureSummary("");
      setFeatureDetails("");
      setFeatureSubmitSucceeded(false);
    }
  };

  const handleOpenChange = (mode: Exclude<ProductFeedbackMode, null>, open: boolean) => {
    if (!open) {
      resetMode(mode);
      setOpenMode(null);
      return;
    }

    setOpenMode(mode);
  };

  const handleBugSubmit = async () => {
    if (!user) return;
    if (bugSubmitInFlightRef.current) return;
    bugSubmitInFlightRef.current = true;
    let attachmentUploadFailed = false;
    let attachmentMetadata: Awaited<ReturnType<typeof uploadBugReportScreenshot>> | null = null;

    try {
      if (bugAttachmentFile) {
        try {
          setIsUploadingBugAttachment(true);
          attachmentMetadata = await uploadBugReportScreenshot({
            supabase,
            reporterUserId: user.id,
            file: bugAttachmentFile,
          });
        } catch {
          attachmentUploadFailed = true;
        } finally {
          setIsUploadingBugAttachment(false);
        }
      }

      const bugReportResult = await submitBugReport.mutateAsync(
        {
          ...buildBugReportPayload(
            {
              summary: bugSummary,
              details: bugDetails,
            },
            {
              route: location.pathname,
              reporterRole,
              reporterUserId: user.id,
              userAgent: typeof navigator === "undefined" ? undefined : navigator.userAgent,
              logs: getRecentClientLogs(),
            },
          ),
          ...(attachmentMetadata ?? {}),
        },
      );

      if (attachmentMetadata && bugReportResult?.screenshotMetadataPersisted === false) {
        await deleteBugReportScreenshot(supabase, attachmentMetadata).catch(() => undefined);
        toast.error("Bug report sent. The attachment did not attach.");
      } else if (attachmentUploadFailed) {
        toast.error("Bug report sent. The attachment did not upload.");
      } else {
        toast.success("Bug report submitted. Thanks for flagging it.");
      }
      resetMode("bug");
      setOpenMode(null);
    } catch {
      if (attachmentMetadata) {
        await deleteBugReportScreenshot(supabase, attachmentMetadata).catch(() => undefined);
      }

      toast.error("Couldn't submit the bug report. Please try again.");
    } finally {
      bugSubmitInFlightRef.current = false;
      setIsUploadingBugAttachment(false);
    }
  };

  const handleBugAttachmentChange = (file: File | null) => {
    if (!file) {
      setBugAttachmentFile(null);
      return;
    }

    const validation = validateBugReportScreenshot(file);
    if (!validation.ok) {
      setBugAttachmentFile(null);
      toast.error(validation.message);
      return;
    }

    setBugAttachmentFile(file);
  };

  const handleFeatureSubmit = async () => {
    if (!user) return;
    const featureDialogSession = featureDialogSessionRef.current;

    try {
      await submitFeatureRequest.mutateAsync(
        buildFeatureRequestPayload(
          {
            summary: featureSummary,
            details: featureDetails,
          },
          {
            route: location.pathname,
            reporterRole,
            reporterUserId: user.id,
          },
        ),
      );

      if (featureDialogSessionRef.current === featureDialogSession && openMode === "feature") {
        setFeatureSubmitSucceeded(true);
      }
    } catch {
      toast.error("Couldn't submit the idea. Please try again.");
    }
  };

  return (
    <>
      <div className={cn("flex flex-wrap gap-2", compact || iconOnly ? "items-center" : "flex-col", className)}>
        <Button
          type="button"
          variant="outline"
          size={iconOnly ? "icon" : compact ? "sm" : "default"}
          onClick={() => setOpenMode("bug")}
          disabled={!user || isSubmitting}
          aria-label={iconOnly ? "Report bug" : undefined}
          title={iconOnly ? "Report bug" : undefined}
          className={cn("justify-start", compact && "h-8 px-2 text-xs", iconOnly && "h-9 w-9 justify-center")}
        >
          <AlertCircle className={cn("h-4 w-4", !iconOnly && "mr-2")} />
          {!iconOnly && "Report bug"}
        </Button>
        <Button
          type="button"
          variant="outline"
          size={iconOnly ? "icon" : compact ? "sm" : "default"}
          onClick={() => setOpenMode("feature")}
          disabled={!user || isSubmitting}
          aria-label={iconOnly ? "Suggest feature" : undefined}
          title={iconOnly ? "Suggest feature" : undefined}
          className={cn("justify-start", compact && "h-8 px-2 text-xs", iconOnly && "h-9 w-9 justify-center")}
        >
          <Lightbulb className={cn("h-4 w-4", !iconOnly && "mr-2")} />
          {!iconOnly && "Suggest feature"}
        </Button>
      </div>

      <ProductFeedbackDialog
        open={openMode === "bug"}
        mode="bug"
        summary={bugSummary}
        details={bugDetails}
        isSubmitting={submitBugReport.isPending || isUploadingBugAttachment}
        onOpenChange={(open) => handleOpenChange("bug", open)}
        onSummaryChange={setBugSummary}
        onDetailsChange={setBugDetails}
        onSubmit={handleBugSubmit}
        attachmentFileName={bugAttachmentFile?.name ?? null}
        onAttachmentChange={handleBugAttachmentChange}
        onAttachmentRemove={() => setBugAttachmentFile(null)}
      />
      <ProductFeedbackDialog
        open={openMode === "feature"}
        mode="feature"
        summary={featureSummary}
        details={featureDetails}
        isSubmitting={submitFeatureRequest.isPending}
        onOpenChange={(open) => handleOpenChange("feature", open)}
        onSummaryChange={setFeatureSummary}
        onDetailsChange={setFeatureDetails}
        onSubmit={handleFeatureSubmit}
        submissionSucceeded={featureSubmitSucceeded}
        onResetSuccessState={() => resetMode("feature")}
      />
    </>
  );
}
